import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router";
import { Button, Spinner } from "@repo/components";
import { useAuth } from "@repo/auth";
import { useCompleteSignup } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { createFirstVenue } from "./createFirstVenue";

/**
 * Self-serve signup for a restaurant owner, in two steps across one Google round trip.
 *
 *   1. NOT SIGNED IN   -> "Continue with Google", which jumps straight to Google via the realm's
 *                         `kc_idp_hint` (no chooser). Keycloak creates the user, or links to an
 *                         existing one by verified email (infra/keycloak-realm/google.tf).
 *   2. SIGNED IN, NO TENANT -> ask for the restaurant name and provision the account.
 *
 * Which step renders is derived from the TOKEN, not from local state, because the Google leg is a
 * full page navigation: the component that started it is gone by the time the user comes back.
 * `tenant_id` present means the account already exists, so this screen has nothing left to do.
 *
 * We never collect a password — Google is the only credential — which is also what makes the later
 * Google Business Profile connection reuse the identity the owner already signed in with.
 */
export function Signup() {
  const { isAuthenticated, isLoading, claims, login } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <SignupCard>
        <Spinner aria-label={t("signup.loading")} />
      </SignupCard>
    );
  }

  // Already provisioned: nothing to sign up for. Sending them to `/` rather than rendering a
  // "you already have an account" dead end — the common way to land here is a stale bookmark.
  if (isAuthenticated && claims.tenantId) {
    return <Navigate to="/" replace />;
  }

  return isAuthenticated ? (
    <AccountNameStep />
  ) : (
    <SignupCard>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "fg.default" })}>
        {t("signup.title")}
      </h1>
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("signup.subtitle")}</p>
      <Button
        size="lg"
        className={css({ w: "full" })}
        onClick={() => {
          login({ idpHint: "google" });
        }}
      >
        {t("signup.cta.google")}
      </Button>
      <p className={css({ color: "fg.muted", fontSize: "xs" })}>{t("signup.noPassword")}</p>
    </SignupCard>
  );
}

/**
 * Step 2 — name the restaurant, then provision.
 *
 * The token is deliberately the ONLY identity input: the endpoint reads the subject from the
 * bearer token and the email from the identity provider, so the body carries the restaurant name
 * and nothing else. Nobody can provision an account for someone else's subject by editing a form.
 */
function AccountNameStep() {
  const { user, refreshClaims } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [accountName, setAccountName] = useState("");
  const [failed, setFailed] = useState(false);

  const { mutate, isPending } = useCompleteSignup();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = accountName.trim();
    if (!trimmed || isPending) return;
    setFailed(false);

    mutate(
      { data: { accountName: trimmed } },
      {
        onSuccess: async () => {
          // MANDATORY, and the reason this handler is async. Claims are attached by the realm's
          // protocol mappers when a token is ISSUED, so the token in hand right now still has no
          // `tenant_id` — navigating straight into the app would make every tenant-scoped call
          // fail until something else happened to refresh it. Get the new token first, then move.
          try {
            await refreshClaims();
          } catch {
            // Provisioning SUCCEEDED; only the refresh failed, so this is not a signup error and
            // must not be reported as one — retrying the mutation would be wrong. A full sign-in
            // mints a token with the claims, so send them through it.
            navigate("/login", { replace: true });
            return;
          }
          // The account exists but owns nothing yet, and an account with no venue is a dead end:
          // the picker says "you are assigned to no establishment" and offers no way out. The owner
          // already told us the restaurant name, so asking again would read as a bug — create it.
          const venueId = await createFirstVenue(trimmed, queryClient);
          navigate(venueId ? `/v/${venueId}/onboarding` : "/", { replace: true });
        },
        onError: () => {
          setFailed(true);
        },
      },
    );
  };

  return (
    <SignupCard>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "fg.default" })}>
        {t("signup.name.title")}
      </h1>
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>
        {user?.email
          ? t("signup.name.subtitleNamed", { email: user.email })
          : t("signup.name.subtitle")}
      </p>

      <form onSubmit={submit} className={css({ w: "full", display: "grid", gap: "4" })}>
        <label htmlFor="accountName" className={css({ srOnly: true })}>
          {t("signup.name.label")}
        </label>
        {/* A native input with theme tokens, matching how every other form in this app renders
            text fields (@repo/components exposes no bare Input). */}
        <input
          id="accountName"
          name="accountName"
          type="text"
          value={accountName}
          onChange={(event) => {
            setAccountName(event.target.value);
          }}
          placeholder={t("signup.name.placeholder")}
          autoComplete="organization"
          required
          disabled={isPending}
          aria-invalid={failed || undefined}
          aria-describedby={failed ? "signup-error" : undefined}
          className={css({
            px: "3",
            py: "2",
            minH: "44px",
            rounded: "md",
            borderWidth: "1px",
            borderColor: "border.default",
            bg: "bg.default",
            fontSize: "md",
            w: "full",
          })}
        />

        {failed ? (
          <p
            id="signup-error"
            role="alert"
            className={css({ color: "fg.error", fontSize: "sm", textAlign: "left" })}
          >
            {t("signup.error")}
          </p>
        ) : null}

        <Button type="submit" size="lg" disabled={isPending || accountName.trim() === ""}>
          {isPending ? t("signup.name.submitting") : t("signup.name.submit")}
        </Button>
      </form>
    </SignupCard>
  );
}

/** The shared centred card, matching [Login] so the two screens read as one flow. */
function SignupCard({ children }: { children: React.ReactNode }) {
  return (
    <main
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minH: "100dvh",
        bg: "bg.canvas",
        px: "4",
        py: "8",
      })}
    >
      <div
        className={css({
          w: "full",
          maxW: "sm",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "6",
          bg: "bg.default",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "xl",
          boxShadow: "lg",
          px: { base: "6", sm: "8" },
          py: "10",
          textAlign: "center",
        })}
      >
        {children}
      </div>
    </main>
  );
}
